import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  UserPlus,
  Mail,
  BarChart3,
  ChevronRight,
  Clock,
  Award,
  BookOpen,
  Brain,
  Activity,
  Trash2,
  X,
  CheckCircle,
  AlertCircle,
  GraduationCap,
  TrendingUp,
  Download,
  FileSpreadsheet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EducatorDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [token] = useState(localStorage.getItem('token'));
  const [activeTab, setActiveTab] = useState('students');
  
  // Data states
  const [students, setStudents] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [classAnalytics, setClassAnalytics] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentProgress, setStudentProgress] = useState(null);
  
  // UI states
  const [isLoading, setIsLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [message, setMessage] = useState(null);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/auth');
      return;
    }
    
    // Check if user has educator subscription
    if (user?.educator_subscription_active) {
      fetchData();
    } else {
      // Try to upgrade (will fail if no subscription)
      upgradeToEducator();
    }
  }, [token, user]);

  const upgradeToEducator = async () => {
    try {
      const response = await fetch(`${API_URL}/api/educator/upgrade`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        // Refresh page to get updated user data
        window.location.reload();
      } else {
        const data = await response.json();
        if (response.status === 403) {
          // Subscription required
          setSubscriptionRequired(true);
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error('Error upgrading to educator:', error);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch students and analytics in parallel
      const [studentsRes, analyticsRes] = await Promise.all([
        fetch(`${API_URL}/api/educator/students`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/educator/class-analytics`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (studentsRes.ok) {
        const data = await studentsRes.json();
        setStudents(data.students || []);
        setPendingInvites(data.pending_invites || []);
      }
      
      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        setClassAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteStudent = async () => {
    if (!inviteEmail) return;
    
    try {
      const response = await fetch(`${API_URL}/api/educator/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: inviteEmail })
      });
      
      if (response.ok) {
        const data = await response.json();
        setInviteCode(data.invite_code);
        setMessage({ type: 'success', text: `Invitation sent to ${inviteEmail}` });
        fetchData();
        setInviteEmail('');
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to send invitation' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send invitation' });
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      const response = await fetch(`${API_URL}/api/educator/invite/${inviteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Invitation cancelled' });
        fetchData();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to cancel invitation' });
    }
  };

  const handleRemoveStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to remove this student from your class?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/educator/student/${studentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Student removed from class' });
        fetchData();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove student' });
    }
  };

  const handleViewProgress = async (student) => {
    setSelectedStudent(student);
    setShowProgressModal(true);
    
    try {
      const response = await fetch(`${API_URL}/api/educator/student/${student.id}/progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStudentProgress(data);
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const handleExportCSV = async (detailed = false) => {
    try {
      const endpoint = detailed ? '/api/educator/export-detailed-csv' : '/api/educator/export-csv';
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = detailed 
          ? `clinicalsim_detailed_report_${new Date().toISOString().split('T')[0]}.csv`
          : `clinicalsim_class_report_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setMessage({ type: 'success', text: 'Report downloaded successfully!' });
      } else {
        setMessage({ type: 'error', text: 'Failed to download report' });
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      setMessage({ type: 'error', text: 'Failed to download report' });
    }
  };

  const renderAnalyticsCards = () => {
    if (!classAnalytics) return null;
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Students</p>
              <p className="text-3xl font-bold text-slate-800">{classAnalytics.total_students}</p>
            </div>
            <div className="p-3 bg-teal-100 rounded-xl">
              <Users className="w-6 h-6 text-teal-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">NCLEX Avg Score</p>
              <p className="text-3xl font-bold text-slate-800">
                {classAnalytics.nclex_stats?.average_score || 0}%
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-xl">
              <Award className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">NCLEX Pass Rate</p>
              <p className="text-3xl font-bold text-slate-800">
                {classAnalytics.nclex_stats?.pass_rate || 0}%
              </p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-xl">
              <TrendingUp className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Simulations</p>
              <p className="text-3xl font-bold text-slate-800">
                {classAnalytics.simulation_stats?.total || 0}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-xl">
              <Activity className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStudentsList = () => (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-6 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Your Students</h2>
          <p className="text-sm text-slate-500">{students.length} students enrolled</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Export Buttons */}
          {students.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleExportCSV(false)}
                className="flex items-center px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm"
                data-testid="export-summary-btn"
                title="Download summary report"
              >
                <Download className="w-4 h-4 mr-1" />
                Summary
              </button>
              <button
                onClick={() => handleExportCSV(true)}
                className="flex items-center px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm"
                data-testid="export-detailed-btn"
                title="Download detailed report with test history"
              >
                <FileSpreadsheet className="w-4 h-4 mr-1" />
                Detailed
              </button>
            </div>
          )}
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            data-testid="invite-student-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Student
          </button>
        </div>
      </div>
      
      {students.length === 0 ? (
        <div className="p-12 text-center">
          <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">No students yet</h3>
          <p className="text-slate-500 mb-4">Invite students to join your class and track their progress</p>
          <button
            onClick={() => setShowInviteModal(true)}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          >
            Send First Invitation
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Student</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Level</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase">NCLEX Tests</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Avg Score</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Simulations</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {students.map(student => (
                <tr key={student.id} className="hover:bg-slate-50" data-testid={`student-row-${student.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white font-semibold">
                        {student.full_name?.charAt(0) || 'S'}
                      </div>
                      <div className="ml-3">
                        <p className="font-medium text-slate-800">{student.full_name}</p>
                        <p className="text-sm text-slate-500">{student.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-slate-100 text-slate-700 text-sm rounded-full">
                      {student.user_level}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="font-medium text-slate-800">{student.nclex_tests_completed || 0}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`font-medium ${
                      (student.nclex_average_score || 0) >= 75 ? 'text-emerald-600' : 'text-amber-600'
                    }`}>
                      {student.nclex_average_score || 0}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="font-medium text-slate-800">
                      {(student.simulations_completed || 0) + (student.advanced_simulations_completed || 0)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleViewProgress(student)}
                      className="text-teal-600 hover:text-teal-700 mr-3"
                      data-testid={`view-progress-${student.id}`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleRemoveStudent(student.id)}
                      className="text-red-500 hover:text-red-600"
                      data-testid={`remove-student-${student.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Pending Invites Section */}
      {pendingInvites.length > 0 && (
        <div className="border-t border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-500 uppercase mb-4">Pending Invitations</h3>
          <div className="space-y-3">
            {pendingInvites.map(invite => (
              <div key={invite.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                <div className="flex items-center min-w-0 flex-1 mr-3">
                  <Mail className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 truncate" title={invite.student_email}>{invite.student_email}</p>
                    <p className="text-sm text-slate-500">Code: {invite.invite_code}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleCancelInvite(invite.id)}
                  className="text-red-500 hover:text-red-600 flex-shrink-0"
                  data-testid={`cancel-invite-${invite.id}`}
                  aria-label={`Cancel invitation for ${invite.student_email}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderInviteModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-800">Invite Student</h2>
          <button onClick={() => { setShowInviteModal(false); setInviteCode(''); }} className="text-slate-400 hover:text-slate-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {inviteCode ? (
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Invitation Sent!</h3>
            <p className="text-slate-600 mb-4">Share this code with your student:</p>
            <div className="bg-teal-600 text-white text-2xl font-mono tracking-widest py-4 px-6 rounded-xl mb-4">
              {inviteCode}
            </div>
            <p className="text-sm text-slate-500 mb-4">
              The student can use this code to join your class when they log in.
            </p>
            <button
              onClick={() => { setShowInviteModal(false); setInviteCode(''); }}
              className="w-full py-3 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <p className="text-slate-600 mb-4">
              Enter the student's email address. They'll receive an invitation with a unique code to join your class.
            </p>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="student@example.com"
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 mb-4"
              data-testid="invite-email-input"
            />
            <div className="flex space-x-3">
              <button
                onClick={() => setShowInviteModal(false)}
                className="flex-1 py-3 border border-slate-300 text-slate-700 rounded-xl font-semibold hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleInviteStudent}
                disabled={!inviteEmail}
                className="flex-1 py-3 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 disabled:opacity-50"
                data-testid="send-invite-btn"
              >
                Send Invitation
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderProgressModal = () => {
    if (!selectedStudent) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto p-4">
        <div className="bg-white rounded-2xl max-w-4xl w-full shadow-xl my-8">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
            <div>
              <h2 className="text-xl font-bold text-slate-800">{selectedStudent.full_name}'s Progress</h2>
              <p className="text-sm text-slate-500">{selectedStudent.email}</p>
            </div>
            <button 
              onClick={() => { setShowProgressModal(false); setStudentProgress(null); }} 
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {!studentProgress ? (
            <div className="p-12 text-center">
              <div className="animate-spin w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-500">Loading progress data...</p>
            </div>
          ) : (
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <BookOpen className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-600">{studentProgress.summary.nclex_tests_completed}</p>
                  <p className="text-sm text-blue-700">NCLEX Tests</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <Brain className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-purple-600">{studentProgress.summary.study_sessions_completed}</p>
                  <p className="text-sm text-purple-700">Study Sessions</p>
                </div>
                <div className="bg-teal-50 rounded-xl p-4 text-center">
                  <Activity className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-teal-600">{studentProgress.summary.simulations_completed}</p>
                  <p className="text-sm text-teal-700">Simulations</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <GraduationCap className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-amber-600">{studentProgress.summary.advanced_simulations_completed}</p>
                  <p className="text-sm text-amber-700">Advanced Sims</p>
                </div>
              </div>
              
              {/* Category Performance */}
              {Object.keys(studentProgress.category_performance || {}).length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-slate-800 mb-3">NCLEX Category Performance</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(studentProgress.category_performance).map(([category, score]) => (
                      <div key={category} className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-700 truncate">{category}</span>
                          <span className={`font-bold ${score >= 75 ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {score}%
                          </span>
                        </div>
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${score >= 75 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                            style={{ width: `${score}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Recent NCLEX Tests */}
              {studentProgress.nclex_tests?.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-slate-800 mb-3">Recent NCLEX Tests</h3>
                  <div className="space-y-2">
                    {studentProgress.nclex_tests.slice(0, 5).map((test, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            test.exam_type === 'lpn' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {test.exam_type?.toUpperCase()}
                          </span>
                          <span className="ml-3 text-sm text-slate-600">
                            {new Date(test.completed_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className={`font-semibold ${
                            (test.score?.percentage || 0) >= 75 ? 'text-emerald-600' : 'text-red-600'
                          }`}>
                            {test.score?.percentage || 0}%
                          </span>
                          {(test.score?.percentage || 0) >= 75 ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500 ml-2" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-red-500 ml-2" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Loading educator dashboard...</p>
        </div>
      </div>
    );
  }

  // Show subscription required screen
  if (subscriptionRequired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <GraduationCap className="w-10 h-10 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-4">Educator Subscription Required</h1>
          <p className="text-slate-600 mb-6">
            The Educator Dashboard is an exclusive feature for subscribers on the Educator plan ($24.99/month). 
            Upgrade to invite students, track their progress, and access class analytics.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/subscription')}
              className="w-full px-6 py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors"
              data-testid="upgrade-to-educator-btn"
            >
              Upgrade to Educator Plan
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-6">
            Students can still join educator classes using invite codes on their dashboard.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="educator-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={() => navigate('/dashboard')} className="text-teal-600 hover:text-teal-700">
                <ChevronRight className="w-6 h-6 rotate-180" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-slate-800 flex items-center">
                  <GraduationCap className="w-6 h-6 mr-2 text-teal-600" />
                  Educator Dashboard
                </h1>
                <p className="text-sm text-slate-500">Manage your students and track their progress</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-slate-600">
                Welcome, {user?.full_name}
              </span>
              <button
                onClick={onLogout}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Message Banner */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl flex items-center ${
            message.type === 'success' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-3" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-3" />
            )}
            <span>{message.text}</span>
            <button onClick={() => setMessage(null)} className="ml-auto">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Analytics Cards */}
        {renderAnalyticsCards()}

        {/* Tabs */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab('students')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'students' 
                ? 'bg-teal-600 text-white' 
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
            data-testid="students-tab"
          >
            <Users className="w-4 h-4 inline mr-2" />
            Students
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'analytics' 
                ? 'bg-teal-600 text-white' 
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
            data-testid="analytics-tab"
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Analytics
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'students' && renderStudentsList()}
        
        {activeTab === 'analytics' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-6">Class Performance Overview</h2>
            
            {classAnalytics?.activity_trend?.length > 0 ? (
              <div>
                <h3 className="text-sm font-medium text-slate-500 mb-4">Activity (Last 7 Days)</h3>
                <div className="flex items-end h-40 space-x-2">
                  {classAnalytics.activity_trend.map((day, idx) => {
                    const maxCount = Math.max(...classAnalytics.activity_trend.map(d => d.count));
                    const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-teal-500 rounded-t-lg transition-all"
                          style={{ height: `${height}%`, minHeight: day.count > 0 ? '8px' : '0' }}
                        />
                        <span className="text-xs text-slate-500 mt-2">
                          {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No activity data yet. Invite students to start tracking!</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Modals */}
      {showInviteModal && renderInviteModal()}
      {showProgressModal && renderProgressModal()}
    </div>
  );
};

export default EducatorDashboard;
